import MainLayout from "components/MainLayout";
import Metadata from "components/Metadata";
import React from "react";

export default function TermsPage() {
  return (
    <MainLayout>
      <Metadata
        title="Terms of Use | Pipers For Hire"
        description="Our terms of use outlines the terms and conditions agreed to by visitors and users of Pipers For Hire."
        url="https://pipersforhire.com/terms"
      />
      <h1 className="text-2xl font-bold text-theme-blue">Terms of Use</h1>
      <p className="mt-2 font-semibold">Last revised 2/2/2023</p>

      <div className="prose mt-20 max-w-screen-lg">
        <h2>Introduction</h2>
        <p>
          Pipers For Hire provides the www.pipersforhire.com website (the
          “Site”) to provide online booking services and promotional tools for
          bagpipe performers, musicians, and clients. In these Terms of Use (the
          “Terms”), the words “Pipers For Hire,” “we,” “us,” and “our” refer to
          Pipers For Hire, LLC, a Tennessee limited liability company, together
          with its affiliates, successors, and assigns. The words “you” and
          “your” refer to any person accessing or using the Services (as defined
          below); however, if you access or use the Services on behalf of a
          company or other entity, you represent that you have authority to bind
          such entity and its affiliates to these Terms and that it is fully
          binding on them. In any such case, the term “you” will refer to such
          an entity and its affiliates.
        </p>

        <p>
          To assist you in using our Site, including all sub-domains thereof, as
          well as our related products and services (collectively, the
          “Services”), and to ensure a clear understanding of the relationship
          arising from your use of our Services, we have created these Terms.
          Together, these Terms and our Privacy Policy govern your use of the
          Services, so please read them both carefully.
        </p>

        <p>
          By using or otherwise accessing our Services, you are acknowledging
          that: (a) you have read and understood these Terms, and (b) you
          unconditionally agree to comply with, and be legally bound by, these
          Terms as they exist on the date of each such use or access. If you
          disagree with any of these Terms in any way, please immediately stop
          all use of Pipers For Hire&apos;s Services. If you have any questions
          or need any clarifications, please contact us before using the
          Services. Please note that these Terms were written in English; to the
          extent any translated version of these Terms conflict with the English
          version, the English version published by Pipers For Hire controls.
        </p>
        <h2>Relationship and Conduct</h2>
        <p>
          1. Incorporation of Introduction. You hereby represent that everything
          in the Introduction, above, and otherwise submitted to us by you
          through the Services or in other communication with us, is true and
          accurate, and you agree that the Introduction is a part of these
          Terms.
        </p>

        <p>
          2. Modification of These Terms. We may modify these Terms at any time.
          Any such modifications will apply prospectively, from the date the
          modifications are made, as designated by the “Last Revised” date
          depicted above. Your use of the Services, or failure to terminate your
          account with us, during thirty (30) days subsequent to any
          modifications will constitute acceptance of the Terms as so changed.
          You will be given notice of each modification by: (a) the updating of
          the “Last Revised” date, above, and (b) an email sent to you by us at
          the address you designate by clicking here, in the event you choose to
          designate an email address. If you choose not to designate an email
          address, you expressly agree that, by updating the “Last Revised” date
          when modifications are made, we are providing you with sufficient
          notice of such modifications, and that you will therefore be bound
          thereby if you continue to use the Services. As such, please check the
          “Last Revised” date prior to each use of the Services by you, as you
          will be bound by these Terms as they exist on the date of each such
          use.
        </p>
        <p>
          3. Reserved Rights. Without limitation to all other rights enjoyed by
          Pipers For Hire, we expressly reserve the right at any time to:
        </p>

        <ol>
          <li> Modify these Terms as provided herein;</li>
          <li>
            Change the Services, including eliminating or discontinuing any
            content on or feature of the Services, and/or adding new features,
            categories, business models, offerings;
          </li>
          <li>
            Terminate the membership of any member without refund and/or pursue
            legal action against any user of this website who does not comply
            with these Terms, though Pipers For Hire assumes no obligation to do
            the same;
          </li>
          <li> Change any fees or charges for using the Services;</li>
          <li>
            Change, edit, add to, disable or delete any Profile information or
            imagery at our sole discretion, or other content within the
            Services, without notice;
          </li>
          <li>
            Terminate any “Client” accounts we feel may be putting our members
            at risk, either because they have not provided a valid phone number
            or because of suspicions we may have regarding the validity of an
            inquiry;
          </li>
          <li>
            Monitor any and all member-to-client and/or member-to-member
            interactions;
          </li>
          <li>
            Revoke any account without refund for acting in a manner Pipers For
            Hire deems inappropriate or unprofessional during any aspect of
            member-to-client or member-to-member interaction, including without
            limitation: initial contact, booking negotiation, performance or
            service provision, and settlement of payment due;
          </li>
          <li>
            Hold a deposit or balance payment if at our sole discretion we
            determine that the booking is a risk for fraud;
          </li>
          <li>
            Require a deposit or balance payment be returned to Pipers For Hire
            if a booking is not honored and/or we determine a violation of these
            Terms has occurred. Failure to return a payment when requested could
            result in permanent removal from the Services; and
          </li>
          <li> Remove any member account at any time for any reason.</li>
        </ol>

        <p>
          4. Online Conduct. At all times when using or accessing the Services,
          you agree that:
        </p>

        <ol>
          <li>
            You are solely responsible for the content or information you
            publish or display (hereinafter, “post”) on the Services or transmit
            to other members.
          </li>
          <li>
            You will not post on the Services, or transmit to other members or
            Pipers For Hire employees, any defamatory, inaccurate, abusive,
            obscene, profane, offensive sexually oriented, threatening,
            harassing, racially offensive, or illegal material, or any material
            that infringes or violates another party&apos;s rights (including,
            but not limited to, intellectual property rights, and rights of
            privacy and publicity). Please contact us in writing if you believe
            any material contained in the Services does not comply with the
            foregoing.
          </li>
          <li>
            In using the Services to enter into any booking agreement or other
            contract of services (a “Contract”), whether you enter into such
            Contract as the talent providing the services or the party
            organizing an event, you will not include in the Contract any terms
            or conditions that are inconsistent with these Terms or with Pipers
            For Hire&apos;s published policies for Contracts entered into
            through our Services (you may find our policies here).
          </li>
          <li>
            You will use the Services in a manner consistent with any and all
            applicable laws and regulations.
          </li>
          <li>
            You will not post any offensive or sexually suggestive references or
            post any photographs or other images containing nudity. Pipers For
            Hire reserves the right, but has no obligation, to remove without
            warning or refund any profile or photograph or image that does not
            comply with these prohibitions.
          </li>
          <li>
            You will maintain current contact information on your profile,
            including at least one main image. We reserve the right to add any
            image or edit any information at any time.
          </li>
          <li>
            You will not falsify your identity or misrepresent yourself in any
            way on the Services or in your Pipers For Hire Profile..
          </li>
          <li>
            You will not “stalk” or otherwise harass any person in any way.
          </li>
          <li>
            You will not engage in advertising to, or solicitation of, other
            members to buy or sell any products or services through the
            Services.
          </li>
          <li>
            You will not transmit any chain letters, spam or junk email to other
            members or to us.
          </li>
          <li>
            You will not express or imply that any statements you make are
            endorsed by us, without our specific prior written consent.
          </li>
          <li>
            You will not harvest or collect personal information about other
            members whether or not for commercial purposes, without our written
            consent.
          </li>
          <li>
            You will not use any robot, spider, site search/retrieval
            application, or other manual or automatic device or process to
            retrieve, index, “data mine”, “scrape” or in any way reproduce or
            circumvent the navigational structure or presentation of the
            Services or its contents.
          </li>
          <li>
            You will not post, distribute or reproduce in any way any
            copyrighted material, trademarks, or other proprietary information
            without obtaining the prior consent of the owner of such proprietary
            rights.
          </li>
          <li>
            You will not interfere with or disrupt our services or the Services,
            or the servers or networks connected to our services or the
            Services.
          </li>
          <li>
            You will not post html code except where instructed in any area of
            your profile within the Services.
          </li>
          <li>
            You will not post, email or otherwise transmit any material that
            contains software viruses or any other computer code, files or
            programs designed to interrupt, destroy or limit the functionality
            of any software, hardware or telecommunications equipment; nor will
            you attempt to disrupt, degrade, impair, or violate the integrity or
            security of the Services in any way (including, without limitation,
            “hacking,” “denial of service” attacks, and the like).
          </li>
          <li>
            You will not forge headers or otherwise manipulate identifiers in
            order to disguise the origin of any information transmitted through
            the Service.
          </li>
          <li>
            You will not “frame” or “mirror” any part of the Services, without
            our prior written authorization. You also shall not use meta tags or
            code or other devices containing any reference to
            www.pipersforhire.com or our Services in order to direct any person
            to any other website for any purpose.
          </li>
          <li>
            You will not modify, copy, adapt, sublicense, translate, sell,
            reverse engineer, create derivative works from, decipher, decompile
            or otherwise disassemble any portion of the Services or any source
            code or other software used in or for the Services, nor will you
            cause, assist, or encourage others to do so.
          </li>
          <li>
            You are solely responsible for your interactions with other members.
            Pipers For Hire reserves the right, but has no obligation, to
            monitor or attempt to resolve disputes between you and other
            members.
          </li>
          <li>
            You will not, for any reason, directly or indirectly submit a review
            to a Profile with which you are affiliated. Reviews may only be
            posted by legitimate clients who have booked you. You may dispute
            any review you feel to be fraudulent, but the removal of reviews is
            solely at our discretion. Posting any illegitimate review, whether
            on your own or on another member&apos;s Profile, may result in your
            account&apos;s immediate termination, at our discretion, without
            refund, and may also result in a permanent ban from the Services.
          </li>
          <li>
            Members are required to keep all bookings and their associated
            communications on the Pipers For Hire platform. We reserve the right
            to terminate any member account that attempts to complete a booking
            outside of Pipers For Hire, when that booking has originated from a
            Pipers For Hire inquiry. Discouraging, suggesting, and/or preventing
            clients from booking you through Pipers For Hire is not permitted,
            and discouraging clients from hiring other Pipers For Hire members
            is not permitted.
          </li>
          <li>
            You will not re-sell or re-offer any lead you receive through Pipers
            For Hire to another performer, vendor or service provider, nor will
            you re-offer or post to any website any lead you receive through
            your relationship with this website.
          </li>
          <li>
            Pipers For Hire has the right to temporarily suspend or permanently
            terminate the account of any member who posts contact information on
            their Pipers For Hire Profile. This contact information includes
            website URL links and addresses, email links and addresses, and
            telephone numbers. Contact information may not be posted in Profile
            photos, in Profile text, in Profile audio/video samples, or in
            messages to prospective clients.
          </li>
          <li>
            You will respond fully, truthfully, and within three (3) business
            days to any request for information or other inquiry from Pipers For
            Hire related to your or another member&apos;s compliance with these
            Terms.
          </li>
        </ol>

        <p>
          Failure to comply with the foregoing provisions outlined under “Online
          Conduct” above may result in immediate suspension or termination of
          your right to use the Services without refunding your membership fees.
          Pipers For Hire reserves the right, in its sole discretion and in all
          instances, to determine whether you have or have not complied with any
          provision contained in these Terms.
        </p>

        <p>
          5. Non-Discrimination Policy. Pipers For Hire supports inclusion and
          wants everyone to feel comfortable using our Site and Services.
          Therefore, we do not allow discrimination against other users of the
          Site, or Pipers For Hire team members, based on race, color,
          ethnicity, national origin, religion, sexual orientation, gender
          identity, marital or pregnancy status, age, disability, or any other
          characteristic protected under applicable federal, regional, state, or
          local law. This includes, but is not limited to, refusing to provide
          or accept services, posting discriminatory content on the Site, or any
          other conduct that improperly takes into account these
          characteristics.
        </p>

        <p>
          If you believe you&apos;ve been discriminated against on our Site,
          please contact us. Failure to comply with this non-discrimination
          policy may result in immediate suspension or termination of your right
          to use the Services without a refund.
        </p>

        <p>
          6. Purpose and Nature of the Services. The contents of our Services,
          and the Services in general, are intended strictly for the purposes
          of: (i) providing a forum for bagpipe performers and musicians to post
          relevant information about themselves, and (ii) facilitating the
          booking of such talent for engagements to provide entertainment
          services (collectively, “Permitted Uses”). Selling or offering to sell
          any interest in real property, any personal property, or other
          tangible good (as opposed to entertainment services) is not a
          Permitted Use of the Services and is strictly prohibited. You
          represent and warrant that you understand, and agree, that Pipers For
          Hire is not, will not be, and will not in any way be construed to be a
          party to any contract or agreement between any users of our Services
          regarding the provisions of services at any event or engagement. While
          we do believe that Pipers For Hire members will achieve some amount of
          exposure from a listing within our Services, we do not guarantee in
          any way that the use of our Services will produce paying clients for
          our members. We do not guarantee that users who supply services as a
          result of introductions made through Pipers For Hire will be paid for
          their services. We do not represent or guarantee that our Services
          will further the careers of our members, help them financially or
          otherwise produce income. Failure to receive the outcome you expected
          does not warrant a refund of payment. Furthermore, Pipers For Hire
          does not take responsibility for the content of any member profile, or
          member services offered on any profile. Pipers For Hire does not
          endorse, sanction or verify, and specifically disclaims responsibility
          or liability for, any member or user profiles. Concerns regarding any
          such service, resource, or link posted or offered by a member should
          be directed to the particular member. Pipers For Hire does not
          endorse, sanction, or verify members on this website. Contacting these
          members, whether through our services or separately is at your own
          risk, and we disclaim all liability with regard to your dealings with
          any member listed within our Services.
        </p>

        <p>
          7. Age Restrictions. In order to use Pipers For Hire&apos;s Site and
          other Services you must be at least 18 years of age, or the legal age
          to form a binding contract in your jurisdiction if that age is greater
          than 18 years of age. Individuals who are under the age of 18 or the
          legal age to form a binding contract in their jurisdiction (“Minors”)
          are prohibited from using the Services. The Services are not intended
          for any children under the age of 13.
        </p>

        <p>
          A legal guardian of a Minor who is at least 13 years of age may create
          a profile for a Minor, and must specify so at the time of
          registration. However, the legal guardian is responsible for
          everything done with that account, and such legal guardian&apos;s
          consent must be verifiable by Pipers For Hire.
        </p>

        <p>
          8. Links to Other Websites and Services. Pipers For Hire does not
          control the availability or content of any outside websites,
          application, services, or resources (collectively, “third party
          services”) to which the Services may link. Concerns regarding any such
          third party services or links thereto should be directed to the
          particular outside third party service. Unless stated otherwise,
          Pipers For Hire does not endorse, sanction, or verify third party
          services to which our Services link, and we or our users provide these
          links merely for the convenience of users of our Services. Access to
          such third party services is at your own risk, and we disclaim all
          liability with regard to your access to such third party services. In
          addition, we do not endorse, sanction, or verify third party services
          that link to our Services, even if any logo or mark of Pipers For Hire
          is used as part of the link to our Services.
        </p>

        <p>
          9. Disclosures About You by Pipers For Hire. Without limiting any of
          the foregoing, you agree that Pipers For Hire may use or disclose
          information about you and your use of the Services in accordance with
          these Terms and our Privacy Policy, which is incorporated by reference
          herein. With respect to any information relating to other users of the
          Services to which you may be permitted access through the Services,
          you agree that you may use such information only in connection with
          activities related to the purpose of the Services, which activities,
          without limitation, shall not include (i) the sending of unsolicited
          communications of a commercial nature; (ii) the sending of any other
          communications after receiving notification that the recipient no
          longer wishes to receive communications from you; or (iii) the sale or
          rental of such personal information to any other parties.
        </p>

        <p>
          10. No Partnership, Agency, or Affiliation. Except only as expressly
          provided otherwise herein, by using the Services you acknowledge and
          agree that Pipers For Hire is not the partner, agent, joint venturer,
          or otherwise an affiliate of you, any member, or any other user of the
          Services. Registration, Payments and Refunds
        </p>

        <p>
          11. Registration and Membership of Users. While you can utilize our
          Services, and review a range of information about entertainers and
          groups of entertainers, as well as other materials relating to the
          entertainment industry, without registering, to post information
          through our Services, and to obtain access to certain other materials
          or information from our Services, you must become a registered user to
          access certain information or complete certain transactions. Our
          registration forms will specify the information which you must provide
          in order to enjoy the particular feature, and the information which
          you may, at your option, choose to provide or withhold. In connection
          with registering at our Services, you agree to provide us with
          accurate, complete, and updated information required by the
          registration form, including, without limitation, your legal name,
          address, telephone number(s), applicable payment data (e.g., credit
          card number and expiration date) and such other information that may
          be required by such forms. Information provided to us by you in
          connection with registering at our Services will be maintained and
          protected by us in accordance with these Terms and with the terms of
          our Privacy Policy.
        </p>

        <p>
          Members shall not have more than one free Profile at any one time if
          they are part of the same affiliation, regardless of location or act
          name specified in the Profile. Any free Profile may be terminated or
          redacted by Pipers For Hire at any time, for any reason, and without
          notice. You hereby grant Pipers For Hire permission to add content to
          your Profile at our sole discretion by uploading content found on the
          website link that you post as your “Official Website” in order to
          increase the quality or presentation of your Profile.
        </p>

        <p>
          When you register to use our Services, you will create a password and
          a user ID, which will provide you access to certain services and
          materials available through the Services unless revoked by us for any
          reason. You agree not to disclose your user ID and password to any
          third party. If at any time you believe that your user ID and/or
          password has been revealed to any other party, or otherwise
          compromised, you agree to notify us in writing immediately and to
          change your user ID and/or password immediately through the member
          control panel within our Services. You agree to be responsible for and
          to indemnify, defend and hold Pipers For Hire, and its officers,
          directors, employees, agents, licensors, and suppliers, harmless from
          any injury, loss or damage we incur on account of any unauthorized use
          of your login name or password. You are solely responsible for
          maintaining the confidentiality of your user ID and password, and for
          any and all activities that are conducted by you or anyone else
          through your account.
        </p>

        <p>
          12. Pipers For Hire&apos;s Fees. Certain parts of the Site and
          Services may be accessed and used without charge. For use of certain
          other parts of our Site and Services, however, you are required to pay
          applicable fees which may change from time to time subject only to
          your right to terminate such Services in accordance with the terms
          thereof. Current fee information is available on the Site. All fees
          due from you to us hereunder, including to the extent applicable,
          transaction-based service fees, monthly fees, annual fees, one-time
          fees, and the like, are referred to herein as “Pipers For Hire Fees.”
          All financial information addressed within the Services (including
          Pipers For Hire Fees, gig quotes, deposits, and/or any other various
          pricing information) is given in terms of US dollars unless otherwise
          stated.
        </p>

        <p>
          13. Payment of Profile Fees. By registering for a Profile, you
          authorize us to charge your credit card, debit card or other payment
          method at such time. You agree that we may charge all Pipers For Hire
          Fees owed in connection with your Profile (the “Profile Fees”) to the
          payment method (e.g., credit card, PayPal account, debit card, etc.)
          you have provided to us. You are solely responsible for the timely
          payment of all Profile Fees. It is your responsibility to notify us if
          your payment method has changed by making the appropriate changes to
          your account settings. If you do not provide a valid payment method,
          or if your designated payment method is determined by us to be
          inactive for any reason, we may immediately discontinue providing
          Services to you. Purchases will appear on your credit or debit card
          statements as “Pipers For Hire.” You agree not to cause your credit or
          debit card company to reverse or “chargeback” any Profile Fees charged
          in accordance with these Terms; and in the event you do so, we may
          terminate your use of the Services, and you agree to reimburse us for
          any costs incurred in responding to such chargeback, including,
          without limitation, our actual costs paid to the credit or debit card
          company, including chargeback fees, and the value of the time our
          employees spend on the matter as determined in our discretion in good
          faith.
        </p>
        <p>
          14. Automatic Renewals. If you choose a paid subscription to a
          Service, your subscription will continue indefinitely until canceled
          by you. After your initial payment of the Profile Fee, your
          subscription will automatically renew on the interval you choose when
          subscribing to that Service. You agree that your subscription will be
          subject to this automatic renewal feature. If you do not wish for your
          subscription to renew automatically, or if you want to change or
          terminate your subscription to a Service, please log in to your
          account and follow the cancellation process. If you cancel your
          subscription to a Service, you may use your subscription until the end
          of your then-current subscription term and your subscription will not
          be renewed thereafter. However, you will not be eligible for a
          prorated refund of any portion of the Profile Fee paid for the
          then-current subscription period. By registering for a Profile, and
          choosing a paid subscription, you authorize us to charge your credit
          card or debit card at the beginning of each renewal subscription
          period. Upon the renewal of your subscription, if we do not receive
          payment, you agree that we may either terminate or suspend your use of
          the Services, or revert your account to a free Profile, thus losing
          any applicable features or benefits of a paid subscription.
        </p>

        <p>
          When you reach the end of a paid subscription, your account will
          revert to a free Profile, thus losing any applicable features or
          benefits of a paid subscription, unless you log in to your account and
          renew your subscription.
        </p>

        <p>
          15. Collection of Deposits and Gig Payments. Each member or other user
          that is a provider of services promoted within the Services (each a
          “Supplier”) hereby appoints Pipers For Hire as its agent for receipt
          of deposit, balance, or other payments that Pipers For Hire permits to
          be made through the Services (each a “Payment”). Where a user that is
          not a Supplier and who wishes to retain a Supplier for services (each
          a “Purchaser”) wishes to make a payment to a Supplier through the
          Services, Pipers For Hire may, but is not required to, accept such
          Payment in its capacity as agent of the Supplier. As of the instant
          when Pipers For Hire is in receipt of any Payment, the Supplier for
          whom the Payment was received hereby releases the Purchaser from any
          liability in respect of the Payment regardless of when or if Pipers
          For Hire settles the Payment amount to Supplier. Purchaser shall be a
          third party beneficiary of such release by Supplier hereunder.
          Following receipt of Payments for Suppliers, Pipers For Hire shall
          settle the amounts thereof to Suppliers less all Pipers For Hire Fees
          disclosed in the Services and less any chargebacks, reversals or
          returns by Purchaser. Pipers For Hire reserves the right to return any
          Payment to a Purchaser for any reason or for no reason. Pipers For
          Hire reserves the right to provide Purchaser with a receipt in the
          name of the Supplier for each Payment which receipt Supplier shall
          honor.
        </p>
        <p>
          16. Refunds. All sales are final and will not be refunded, unless
          specifically agreed to otherwise by us.
        </p>

        <p>Intellectual Property Rights, Licenses, and Complaints</p>
        <p>
          17. Content Ownership. Unless otherwise noted, all copyrighted works,
          trademarks, service marks, trade dress, or other intellectual
          properties depicted in any texts, articles, images, illustrations,
          designs, icons, photographs, images, videos, sound clips, or any other
          materials within the Services (collectively, the “Contents”) are
          owned, licensed by, used with permission, or otherwise lawfully used
          by Pipers For Hire. The Services as a whole is protected by copyright,
          trademark, trade dress and other proprietary rights, all worldwide
          right, title and interest in and to which is owned and controlled by
          Pipers For Hire. Unless otherwise noted, by using a third party&apos;s
          trademark or trade name within our Services, Pipers For Hire does not
          intend to suggest any affiliation with or endorsement of such third
          party or its products or services, and Pipers For Hire hereby
          expressly disclaims any such affiliation or endorsement.
        </p>

        <p>
          18. Site and Services Use License Subject to these Terms and only to
          the extent expressly permitted by the functionality of the Site and
          Services, you are granted a limited, non-exclusive, revocable,
          non-assignable, and non-transferable right to use the Site and
          Services and access and interact with Contents. Except with respect to
          your use of certain Services that provide for or facilitate commercial
          activities, you understand that the Site and Contents are for your
          personal and non-commercial use only. Except as expressly permitted by
          these Terms, the functionality of the Site (i) no Contents (other than
          your Submissions) may be modified, copied, distributed, framed,
          reproduced, republished, downloaded, displayed, posted, transmitted,
          sold, or otherwise used in any form or by any means, in whole or in
          part, and (ii) nothing herein should be construed as granting any
          license or right to use any Contents, including trademarks, service
          marks, logos, slogans or taglines of us or third parties, or any
          software or code relating to Pipers For Hire&apos;s Site or other
          Services.
        </p>
        <p>
          19. License to Submissions. With respect to any posts, text,
          photographs, names, images, likenesses, trademarks, service marks,
          logos, designs, images, video clips, music, musical work, sound
          recording, sound clips, media files, or other information or materials
          you may provide to Pipers For Hire for posting on your Profile
          (collectively, “Submissions”), such Submissions shall remain your
          property or your licensors&apos; property. You hereby unconditionally
          and irrevocably grant to Pipers For Hire and its affiliates and
          licensees a non-exclusive, worldwide, transferrable, and royalty-free
          right and license to host, copy, reproduce, distribute, transmit,
          stream, publicly perform, publicly display, modify, publish, edit,
          create derivative works from, or otherwise use the Submissions in any
          and all media, now known or hereafter devised, solely in connection
          with the Services and/or advertisements or other materials promoting
          the Services.
        </p>

        <p>
          Pipers For Hire shall have no obligation to pay you or anyone else any
          amounts, including record or publishing royalties, performance fees,
          license fees, or union, guild, or collective bargaining fees, for the
          distribution, public performance, or other uses of your Submissions as
          authorized in these Terms. The foregoing licenses and rights granted
          to Pipers For Hire will survive your removal of Submissions from the
          Services, the termination of your account use of the Services, or any
          other termination of these Terms unless we receive written notice of
          termination of such license(s) from you, at which point such
          license(s) will expire and we will stop using the specified
          Submissions within thirty (30) days. You agree to keep a copy of any
          Submissions you provide, as Pipers For Hire shall have no obligation
          to return any Submissions to you should it become damaged or lost.
        </p>
        <ol>
          <li> You represent, warrant and agree that:</li>
          <li>
            You own or control all necessary rights in and to the Submissions to
            use them with Pipers For Hire;
          </li>
          <li>
            You have the full right and authority to grant the rights, licenses,
            and permissions in these Terms;
          </li>
          <li>
            All Submissions submitted by you into or through the Services do not
            and will not violate any rights of any third party, including,
            without limitation, copyright, trademark, privacy or other personal
            or proprietary right(s); and
          </li>
          <li>
            To the extent you do not own any underlying musical compositions,
            sound recordings, samples, publicity rights, or other materials or
            content in your Submissions, it is your obligation to secure all
            appropriate permissions, clearances, licenses, or other
            authorizations and agreements, and, as applicable, to pay directly
            all third-party fees and royalties as may be required, including,
            but not limited to, with respect to artists, band members, authors,
            co-authors, copyright owners and co-owners, publishers, producers,
            or any other person who performed in the making of your Submissions,
            and in connection with mechanical royalties, digital phonograph
            delivery royalties, public performance royalties, payments that may
            be required under any collective bargaining agreements or statutory
            schemes, or any other royalties, fees, and/or sums payable with
            respect to your Submissions, and at our request you shall provide us
            with confirmation of such authorizations and payments.
          </li>
        </ol>

        <p>
          20. Complaints About Perceived Trademark Infringement. Pipers For Hire
          respects intellectual property rights, and may remove content,
          terminate registered users, and deny access to others who, in our
          discretion, infringe the intellectual property rights of others. Once
          you have determined that a violation of your trademark rights
          (“infringement” for the purposes of this Section 20) has occurred, we
          encourage you to contact the Pipers For Hire user responsible for the
          content about the perceived infringement. You and the user may be able
          to resolve the issue quickly without Pipers For Hire&apos;s
          involvement. To be considered a complete claim, you will be required
          to provide all of the following information:
        </p>
        <ul>
          <li>
            Your full name, mailing address, email address, and phone number;
          </li>
          <li>
            The specific word, term, design, image or other device in which you
            claim trademark rights (“Your Mark”);
          </li>
          <li>
            A summary of the goods and/or services sold or offered by you under
            Your Mark;
          </li>
          <li>
            The United States Patent and Trademark Office (USPTO) for Your Mark,
            if registered;
          </li>
          <li>
            The URL, or other information, sufficient to permit Pipers For Hire
            to locate and identify the material you contend infringes Your Mark;
          </li>
          <li>
            A detailed description of how you believe the reported material
            infringes Your Mark;
          </li>
          <li>
            A declaration, under penalty of perjury, that either (a) you are the
            owner of Your Mark, or (b) you are an authorized representative of
            the owner (including an explanation of your relationship to the
            owner of Your Mark);
          </li>
          <li>
            A declaration, under penalty of perjury, that: (a) you believe in
            good faith that content complained of is not authorized by the owner
            of Your Mark, its agent, or the law; and (b) the information
            contained in the completed claim form is accurate in all respects;
            and
          </li>
          <li>Your signature.</li>
        </ul>

        <p>
          Pipers For Hire will process any claim complying with the foregoing.
          If we believe, in our sole discretion, that you have established a
          prima facie claim of trademark infringement, we will: (a) promptly
          suspend the member or disable access to the complained-of material,
          (b) notify the member of your claim, and (c) remove the material or
          member from the Services or take other action(s) to address the
          perceived infringement; however, if such member promptly provides
          Pipers For Hire with persuasive evidence or arguments, in Pipers For
          Hire&apos;s discretion, that the complained-of materials do not
          infringe Your Mark and should not have been removed, we will reinstate
          the member and/or restore the materials. If we decide to restore any
          such material, we will notify you and encourage you to reach out
          directly to the member about your claim.
        </p>

        <p>
          Please be advised that we may, in our discretion, provide an alleged
          infringer with any communications from you about the perceived
          infringement, including the claim form submitted by you, as well as
          any contact information we have on file for you. We may also provide
          any responses received and communication related thereto, to the
          provider of the original notification of perceived infringement.
        </p>

        <p>
          Without limiting Pipers For Hire&apos;s other rights set forth herein,
          any user who engages in “repeat infringement” may be terminated from
          the Services without notice, and may no longer be permitted access to
          the Services. “Repeat infringement” shall be defined as two (2) or
          more instances, as determined by us in our discretion, where a user
          has infringed the trademark rights of another person. Notwithstanding
          the foregoing, we reserve the right to terminate members that are the
          subject of fewer than two (2) instances of infringement of someone
          else&apos;s trademark rights in appropriate circumstances, such as
          (for example) when the member has a history of violating or willfully
          disregarding our Terms.
        </p>

        <p>
          21. Complaints About Perceived Copyright Infringement. Pipers For Hire
          respects intellectual property rights, and may remove content,
          terminate registered users, and deny access to others who, in our
          discretion, infringe the intellectual property rights of others.
        </p>

        <p>
          If you are concerned that any content within our Services infringes
          any rights you enjoy under copyright law in specific material (a
          “Work”), please seek legal advice. You may also read basic facts about
          copyrights, published by the United States Copyright Office, here.
        </p>

        <p>
          Once you have determined that a violation of your copyright interests
          (“infringement” for the purposes of this Section 21) has occurred
          relative to Work you created or own, you must follow the procedures
          set forth below.
        </p>

        <p>
          21.A. User-Generated Content; DMCA. If you believe that content within
          our Services that was generated by a member or other user infringes
          your copyright interests in your Work, we encourage you first to
          contact the user responsible for the content about the perceived
          infringement. You and the user may be able to resolve the issue
          quickly without Pipers For Hire&apos;s involvement.
        </p>

        <p>
          If you believe the perceived copyright infringement requires action by
          Pipers For Hire, you must submit to Pipers For Hire a formal takedown
          notice (a “Takedown Notice”) fully compliant with the Digital
          Millennium Copyright Act (“DMCA”). The DMCA establishes the procedures
          to be followed when Pipers For Hire receives a Takedown Notice from a
          putative copyright holder or agent of a copyright holder (hereafter
          referred to as the “Complainant”). Submitting a Takedown Notice is a
          serious matter that has legal consequences, and you may not submit a
          Takedown Notice based on perceived infringement of someone else&apos;s
          copyright interests, or of legal interests other than copyright
          interests.
        </p>
        <p>
          You may use this claim form provided by Pipers For Hire, or may
          prepare your own Takedown Notice. At a minimum, any Takedown Notice
          submitted to Pipers For Hire for processing must contain at least the
          following:
        </p>
        <ul>
          <li>
            An identification of the infringed copyrighted work, or, if multiple
            copyrighted works are covered by a single Takedown Notice, a list of
            all works alleged to have been infringed;
          </li>
          <li>
            An identification of the alleged infringing material and information
            reasonably sufficient to permit us to locate the material;
          </li>
          <li>
            Contact information of the Complainant, such as an address,
            telephone number, electronic mail address;
          </li>
          <li>
            A statement that the Complainant has a good faith belief that use of
            the material in the manner complained of is not authorized by the
            copyright owner, its agent, or the law;
          </li>
          <li>
            A statement that the information contained in the Takedown Notice is
            accurate, and under penalty of perjury, that the Complainant is
            authorized to act on behalf of the owner of an exclusive copyright
            interest that is being infringed; and
          </li>
          <li> A physical or electronic signature of the Complainant.</li>
        </ul>

        <p>
          Upon receipt of a Takedown Notice Pipers For Hire deems substantially
          complete and valid, we will follow the notice, takedown and
          counter-notification procedures set forth in section 512(c) of the
          DMCA, beginning with expeditiously removing, or disabling access to,
          the complained-of material. Please be advised that we may provide an
          alleged infringer with any communications about the claimed
          infringement, including the Takedown Notice submitted by you. We may
          also provide any counter notifications received and communication
          related thereto, to the provider of the original notice of claimed
          infringement, as well as any contact information we have on file for
          the alleged infringer.
        </p>

        <p>
          Without limiting Pipers For Hire&apos;s other rights set forth herein,
          any user who engages in “repeat infringement” may be terminated from
          the Services without notice, and may no longer be permitted access to
          the Services. “Repeat infringement” shall be defined as two (2) or
          more instances, as determined by us in our discretion, where a user
          has infringed the exclusive copyright interests of another person.
          Notwithstanding the foregoing, we reserve the right to terminate
          members that are the subject of fewer than two (2) instances of
          infringement of someone else&apos;s exclusive copyright interests in
          appropriate circumstances, such as (for example) when the member has a
          history of violating or willfully disregarding our Terms.
        </p>

        <p>
          21.B. Other Content. If you believe the perceived copyright
          infringement involves content generated by Pipers For Hire, rather
          than members or other users of the Services, please contact us with a
          detailed explanation of the perceived infringement. We will review
          what you send and promptly take appropriate action, in our discretion,
          if any action is required.
        </p>

        <h2>Disputes, Liabilities, and Disclaimers</h2>

        <p>
          22. DISCLAIMER OF ALL WARRANTIES. All data and information featured in
          the Services is provided strictly “AS-IS” and without any warranties
          of any kind, either express or implied, including without limitation
          warranties of title, accuracy, truth, reliability, completeness,
          currency, non-infringement, or any implied warranties of
          merchantability or fitness for a particular purpose. Further, we
          specifically make no representation or warranty regarding, and we
          disclaim and assume no liability or responsibility regarding: (i) the
          quality or availability of any postings made by you or any other users
          of the Services; (ii) whether any scheduled events or engagements will
          occur; (iii) whether persons or acts contacted through the Services
          will appear at any events or engagements; (iv) the ability of you or
          any other user of the Site to engage in transactions with any other
          user of the Services; (v) any details relating to any transactions you
          may engage in with any other use of the Services; (vi) the quality of
          the listings or other information offered by you or any user of the
          Services; (vii) the financial condition of you or any user of the
          Services; or (viii) compliance with local, state and federal laws by
          you or any other user of the Services. Pipers For Hire makes no
          representation that the information in the Services is appropriate or
          available for use in locations outside the United States, and access
          to the Services from locations where the provision of such information
          may be illegal is prohibited. Users who determine to access the
          Services from such locations do so on their own initiative and are
          responsible for compliance with all applicable local laws.
        </p>

        <p>
          23. LIMITATION OF LIABILITY. Neither Pipers For Hire nor any person or
          entity affiliated or involved with creating, producing, hosting,
          maintaining or delivering the Services shall be liable for any direct,
          incidental, consequential, special, indirect, or punitive damages
          arising out of your, or any other party&apos;s access to or use of the
          Services. Your sole remedy as a result of any breach or alleged breach
          by us of these Terms is limited to your ability to discontinue use of
          the Site, and your ability to terminate your participation in any
          Services. To the maximum extent allowed by applicable law, Pipers For
          Hire shall have no liability or responsibility for any errors,
          omissions, or inaccuracies in the content within the Services or
          information flowing through the Services.
        </p>

        <p>
          24. ASSUMPTION OF RISK. Your use of the Services is at your sole risk.
          You should conduct your own independent investigation of the users of
          the Services before entering into any transaction with such persons.
          In no event shall we or any other party involved in creating,
          producing, or delivering the Services be liable for any financial or
          other obligations that may arise between you and any other user of the
          Services.
        </p>

        <p>
          You acknowledge that documents posted to and content available through
          the Services may contain computer viruses and you expressly assume
          that risk. Should you download any such materials from the Services,
          it is your responsibility to perform any scanning or other anti-virus
          precautions as you deem necessary. Under no circumstances will we be
          responsible for any loss or damage caused by any materials downloaded
          from the Services. You assume full responsibility for all costs
          associated with all necessary servicing or repairs of any equipment
          you use in connection with your use of the Services.
        </p>

        <p>
          25. WAIVER AND RELEASE. By using our Services, you hereby waive any
          rights of recovery, under any theory of law, that you may have against
          Pipers For Hire, and its parents, subsidiaries and affiliates, and you
          release Pipers For Hire from any claim, cause of action or demand, on
          account of your use of the Services or the provision of the Services
          by Pipers For Hire.
        </p>

        <p>
          26. Indemnification. By using the Services, you agree to indemnify,
          defend, save and hold harmless Pipers For Hire, and its members,
          managers, employees, agents, representatives, licensors, affiliates,
          parents, subsidiaries, subcontractors, and partners (collectively, our
          “Related Parties”) against any loss, claims, judgments, damage,
          demands, cost, or expenses (including reasonable attorneys&apos; fees
          and costs), arising out of or in connection with: (i) your use of the
          Services; (ii) any breach or violation by you of these Terms; (iii)
          any breach or violation by you of any representation or warranty made
          herein; and/or (iv) your violation of any rights of any third party.
          Without limitation, you agree to reimburse us and our Related Parties
          on demand for any payments made in resolution of any liability or
          claim that is subject to indemnification under this Section 26. We
          will promptly notify you of any such claim, and you may assume control
          of the defense of such claim with counsel subject to our reasonable
          approval, provided that you obtain and post an appropriate bond for
          our benefit, and we shall have the right in all events to participate
          in the defense thereof. If a claim is made we will have the right to
          withhold payment of any monies due you in an amount reasonably related
          to the claim and potential expenses.
        </p>

        <p>
          27. Applicable Law. These Terms and Pipers For Hire&apos;s provision
          of the Services to you shall be governed by and construed in
          accordance with the laws of the State of Tennessee, U.S.A., without
          reference to conflict of laws provisions thereof and, as to matters
          affecting copyrights, trademarks and patents, by U.S. federal law.
        </p>

        <p>
          28. Exclusive Jurisdiction. You and Pipers For Hire further agree that
          any dispute, claim, or action to interpret or enforce these Terms, or
          in any way related to your use of, or Pipers For Hire&apos;s provision
          of, the Services, may only be brought exclusively in the state or
          federal courts of Tennessee. You agree to submit to the personal
          jurisdiction of such courts for the purposes of litigating any such
          claim(s).
        </p>

        <p>
          29. Enforcement Costs. Should either of us employ an attorney or
          attorneys to enforce any the provision(s) of these Terms, the
          non-prevailing party in any final judgment agrees to pay the
          prevailing party&apos;s reasonable expenses, including attorneys&apos;
          fees and expenses, incurred in connection with such enforcement action
          and efforts, as determined by a court of competent jurisdiction.
        </p>

        <h2>Miscellaneous</h2>

        <p>
          34. Notice to California Users. Under California Civil Code Section
          1789.3, California Services members are entitled to the following
          specific consumer rights notice: The Complaint Assistance Unit of the
          Division of Consumer Services of the California Department of Consumer
          Affairs may be contacted in writing at 1625 N. Market Blvd., Suite N
          112, Sacramento, California 95834, or by telephone at (800) 952-5210.
        </p>

        <p>
          35. Contacting Us. If you have any feedback, questions, concerns, or
          comments about any of the Services, please contact us as provided
          here. Please be sure to include in any email or postal mail your full
          name, email address, postal address, and any message.
        </p>

        <p>
          36. Modifications to the Services. We may make improvements or other
          changes to the Services, or to the information within and services
          offered through the Services, without notice to you. We may add,
          delete, move, or modify some or all of the Services at any time.
        </p>

        <p>
          37. Transfer of Rights and Assignment. Pipers For Hire may wish at
          some point to assign or transfer its rights in the Services to a third
          party, whether in connection with a merger, acquisition, asset sales,
          reorganization or otherwise. You agree that Pipers For Hire may
          transfer or assign to a third party all of its rights and obligations
          with respect to you, including any rights and obligations with respect
          to your Personally Identifiable Information, on the condition that
          such third party agree to abide by a privacy policy at least as
          protective as Pipers For Hire&apos;s then-applicable Privacy Policy.
        </p>

        <p>
          38. Relationship. You agree that no joint venture, partnership,
          employment, or agency relationship exists between you and Pipers For
          Hire as a result of these Terms or your use of the Services.
        </p>

        <p>
          39. Severability. Invalidation of any provision of these Terms shall
          not impair or affect in any manner the validity and enforceability of
          the remaining portions, which in such event shall remain in full force
          and effect as if such invalid provision had never been included
          herein.
        </p>

        <p>
          40. Captions. The captions herein are inserted only as a matter of
          convenience and in no way define, limit, describe or otherwise affect
          the scope or interpretation of this Agreement.
        </p>

        <p>
          41. Entire Agreement; Amendments and Waiver. These Terms comprise the
          entire agreement between you and Pipers For Hire regarding your use of
          the Services, and they supersede all prior understandings regarding
          the same. If we fail to enforce any of our rights arising out of these
          Terms, such failure shall not constitute a waiver thereof. To be valid
          and enforceable, any amendment to or waiver of these Terms must be
          made in writing, signed or posted within the Services by Pipers For
          Hire.
        </p>
      </div>
    </MainLayout>
  );
}
